import { useCallback, useState } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'

import { storePendingToast } from 'Utils/pendingToast'
import { NOOP } from 'Utils/stableDefaults'

const DEFAULT_RETURN_PATH = '/profile/settings#tab-settings-subscription'
const DEFAULT_COMMIT_URL = '/profile/subscription/upgrade'
// Give the async upgrade engine (charge + provisioning + webhooks) time to settle before
// landing on account settings, mirroring the payment form's success delay.
const SUCCESS_REDIRECT_DELAY_MS = 5000

const EMPTY_TRACKING = {
  trackPayClick: NOOP,
  trackSuccess: NOOP,
  trackPaymentMethodSelection: NOOP,
  trackFailure: NOOP,
}

// Drives the Essentials->Premium upgrade checkout payment flow.
//
// Two paths, one CTA:
//   - Saved method (default): POST the commit route directly; the upgrade engine charges
//     the card already on file.
//   - New method: submit the Solidgate update form (an amount-0 auth intent that saves the
//     new card via the `card_gate.order.updated` webhook). On success we commit the upgrade,
//     so the engine charges the just-updated method.
// Wallet buttons (Google/Apple Pay, PayPal) inside SolidgatePaymentSection self-submit and
// route through the same success handler, so they commit the upgrade too.
//
// Tracking: the saved path fires pay-click + success here; the new path's pay-click, method
// selection and auth success/failure are fired by SolidgatePaymentSection (which owns the
// SDK events), so we do not double-track them on the commit.
export default function useUpgradeCheckout({
  paymentMethod = null,
  commitUrl = DEFAULT_COMMIT_URL,
  returnPath = DEFAULT_RETURN_PATH,
  tracking = EMPTY_TRACKING,
}) {
  const { postData } = useApiPost()

  const hasSavedMethod = Boolean(paymentMethod)

  const [useSavedMethod, setUseSavedMethod] = useState(hasSavedMethod)
  const [isDifferentOpen, setIsDifferentOpen] = useState(!hasSavedMethod)
  const [paymentForm, setPaymentForm] = useState(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const { trackPayClick, trackSuccess, trackPaymentMethodSelection, trackFailure } = tracking

  const commitUpgrade = useCallback((onCommitted = NOOP) => {
    postData({
      path: commitUrl,
      onSuccess: (response) => {
        const data = response?.data || {}

        // Guards (e.g. #upgrade eligibility) answer with { redirectTo, message } at HTTP 200,
        // so honor that instead of treating every 200 as a committed upgrade. Mirrors the
        // checkout's fetchPaymentData redirect handling.
        if (data.redirectTo) {
          setSubmitting(false)
          const type = data.flashType === 'notice' ? 'success' : 'error'
          storePendingToast({ type, message: data.message })
          window.location.assign(data.redirectTo)
          return
        }

        onCommitted()
        setIsProcessing(true)
        setTimeout(() => { window.location.assign(returnPath) }, SUCCESS_REDIRECT_DELAY_MS)
      },
      onError: () => {
        setSubmitting(false)
        trackFailure('upgrade_commit_failed')
      },
    })
  }, [postData, commitUrl, returnPath, trackFailure])

  const handleSelectSaved = useCallback(() => {
    setUseSavedMethod(true)
    trackPaymentMethodSelection('saved')
  }, [trackPaymentMethodSelection])

  const handleToggleDifferent = useCallback(() => {
    const opening = !isDifferentOpen
    setIsDifferentOpen(opening)
    setUseSavedMethod(!opening)
  }, [isDifferentOpen])

  const handleFormReady = useCallback((form) => {
    setPaymentForm(form)
  }, [])

  const handleFormValidityChange = useCallback((valid) => {
    setIsFormValid(valid)
  }, [])

  const handlePaymentProcessing = useCallback(() => {
    setSubmitting(true)
  }, [])

  const handlePaymentSuccess = useCallback(() => {
    commitUpgrade()
  }, [commitUpgrade])

  const handlePay = useCallback(() => {
    if (submitting) return

    if (useSavedMethod) {
      if (!hasSavedMethod) return

      setSubmitting(true)
      trackPayClick()
      commitUpgrade(trackSuccess)
      return
    }

    // New card path: submit the Solidgate form; SDK events + success flow back through
    // SolidgatePaymentSection and handlePaymentSuccess, which commits the upgrade.
    if (paymentForm) {
      paymentForm.submit()
    }
  }, [submitting, useSavedMethod, hasSavedMethod, paymentForm, commitUpgrade, trackPayClick, trackSuccess])

  const isPayDisabled = submitting
    || (useSavedMethod && !hasSavedMethod)
    || (!useSavedMethod && !isFormValid)

  return {
    useSavedMethod,
    isDifferentOpen,
    submitting,
    isProcessing,
    isPayDisabled,
    handleSelectSaved,
    handleToggleDifferent,
    handleFormReady,
    handleFormValidityChange,
    handlePaymentProcessing,
    handlePaymentSuccess,
    handlePay,
  }
}
